import React, { useMemo } from 'react';
import { CheckCircle, Grid3x3, Package, Percent, Maximize2 } from 'lucide-react';

const CuttingPlanPreview = ({ calculationResult, planData, onFinish }) => {
  const colors = useMemo(() => [
    'bg-blue-200 border-blue-500',
    'bg-green-200 border-green-500',
    'bg-yellow-200 border-yellow-500',
    'bg-purple-200 border-purple-500',
    'bg-pink-200 border-pink-500',
    'bg-indigo-200 border-indigo-500',
    'bg-red-200 border-red-500',
    'bg-teal-200 border-teal-500'
  ], []);

  const getColor = (index) => colors[index % colors.length];

  if (!calculationResult) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Nenhum resultado de cálculo disponível</p>
      </div>
    );
  }

  const { sheets, efficiency, totalQuantity, actualSheets, usedArea, wasteArea, totalArea } = calculationResult;

  const canvasWidth = 400;
  const scale = canvasWidth / (calculationResult.sheets[0]?.width || 2750);

  return (
    <div className="space-y-6">
      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-6">
        <div className="flex items-center gap-3 mb-4">
          <CheckCircle className="w-8 h-8 text-emerald-600" />
          <div>
            <h3 className="text-lg font-semibold text-emerald-900">Plano Gerado com Sucesso!</h3>
            <p className="text-sm text-emerald-700">
              O plano de corte foi criado e está pronto para uso.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white border border-gray-200 rounded-lg p-4 flex items-center gap-3">
          <div className="p-3 bg-blue-50 rounded-lg">
            <Package className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-900">{totalQuantity}</p>
            <p className="text-sm text-gray-500">Peças Totais</p>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4 flex items-center gap-3">
          <div className="p-3 bg-green-50 rounded-lg">
            <Grid3x3 className="w-6 h-6 text-green-600" />
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-900">{actualSheets}</p>
            <p className="text-sm text-gray-500">Chapas Usadas</p>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4 flex items-center gap-3">
          <div className="p-3 bg-purple-50 rounded-lg">
            <Percent className="w-6 h-6 text-purple-600" />
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-900">{efficiency}%</p>
            <p className="text-sm text-gray-500">Aproveitamento</p>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4 flex items-center gap-3">
          <div className="p-3 bg-amber-50 rounded-lg">
            <Maximize2 className="w-6 h-6 text-amber-600" />
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-900">{usedArea} m²</p>
            <p className="text-sm text-gray-500">Área Útil</p>
          </div>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Visualização do Corte</h3>

        <div className="space-y-6">
          {sheets.map((sheet, sheetIndex) => (
            <div key={sheetIndex} className="border border-gray-200 rounded-lg p-4">
              <h4 className="font-medium text-gray-700 mb-3">
                Chapa {sheet.sheetNumber} - {sheet.width} x {sheet.height} mm ({sheet.pieces.length} peças)
              </h4>

              <div className="relative bg-gray-100 border-2 border-gray-300 overflow-hidden"
                style={{
                  width: canvasWidth,
                  height: Math.round((sheet.height / sheet.width) * canvasWidth),
                  maxWidth: '100%'
                }}
              >
                <svg
                  width={canvasWidth}
                  height={Math.round((sheet.height / sheet.width) * canvasWidth)}
                  viewBox={`0 0 ${canvasWidth} ${Math.round((sheet.height / sheet.width) * canvasWidth)}`}
                  className="absolute inset-0"
                >
                  {sheet.pieces.map((piece, pieceIndex) => {
                    const x = piece.x * scale;
                    const y = piece.y * scale;
                    const w = piece.width * scale;
                    const h = piece.height * scale;

                    return (
                      <g key={pieceIndex}>
                        <rect
                          x={x}
                          y={y}
                          width={w}
                          height={h}
                          className={`${getColor(pieceIndex)} stroke-2`}
                          fillOpacity={0.6}
                        />
                        <text
                          x={x + w / 2}
                          y={y + h / 2}
                          textAnchor="middle"
                          dominantBaseline="middle"
                          className="text-xs font-medium fill-gray-800"
                        >
                          {piece.partCode}
                        </text>
                      </g>
                    );
                  })}
                </svg>
              </div>

              <div className="mt-3 flex flex-wrap gap-2">
                {sheet.pieces.map((piece, idx) => (
                  <div
                    key={idx}
                    className={`px-2 py-1 border rounded text-xs ${getColor(idx)}`}
                  >
                    {piece.partCode}: {piece.width}x{piece.height}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-between items-center pt-4 border-t border-gray-200">
        <div className="text-sm text-gray-500">
          Plano: {planData.description || 'Sem descrição'} |
          Chapa: {planData.boardWidth * 1000} x {planData.boardHeight * 1000} mm
        </div>

        <button
          onClick={onFinish}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <CheckCircle className="w-5 h-5" />
          Ver Plano Completo
        </button>
      </div>
    </div>
  );
};

export default CuttingPlanPreview;
